import React, { useState, useEffect, useCallback } from 'react';
import { 
  History, Calendar, Filter, User, 
  Loader2, AlertCircle, ChevronLeft, ChevronRight,
  FileText, DollarSign, Users, Building2, RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api from '../lib/api';

const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getActivityIcon = (activityType) => {
  if (activityType?.includes('project')) return Building2;
  if (activityType?.includes('transaction')) return DollarSign;
  if (activityType?.includes('employee') || activityType?.includes('ledger')) return Users;
  if (activityType?.includes('jv') || activityType?.includes('note')) return FileText;
  return History;
};

const getActivityColor = (activityType) => {
  if (activityType?.includes('created')) return 'bg-green-500/20 text-green-400 border-green-500/30';
  if (activityType?.includes('deleted')) return 'bg-red-500/20 text-red-400 border-red-500/30';
  if (activityType?.includes('updated')) return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
  return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
};

const ActivityTypeLabel = (type) => {
  if (!type) return 'Unknown';
  return type
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
};

export const ActivityHistory = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [activityType, setActivityType] = useState('all');
  const [entityType, setEntityType] = useState('all');
  const [userEmail, setUserEmail] = useState('');
  
  // Pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  
  // Available filter options
  const [activityTypes, setActivityTypes] = useState([]);
  const [entityTypes, setEntityTypes] = useState([]);

  // Fetch activity types for filter
  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const response = await api.get('/activity-history/types');
        setActivityTypes(response.data.activity_types || []);
        setEntityTypes(response.data.entity_types || []);
      } catch (err) {
        console.error('Failed to fetch activity types:', err);
      }
    };
    fetchTypes();
  }, []);

  // Fetch activities
  const fetchActivities = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (activityType && activityType !== 'all') params.append('activity_type', activityType);
      if (entityType && entityType !== 'all') params.append('entity_type', entityType);
      if (userEmail) params.append('user_email', userEmail);
      params.append('limit', limit.toString());
      params.append('skip', ((page - 1) * limit).toString());
      
      const response = await api.get(`/activity-history?${params.toString()}`);
      setActivities(response.data.activities || []);
      setTotalCount(response.data.total_count || 0);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load activity history');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, activityType, entityType, userEmail, page, limit]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const totalPages = Math.ceil(totalCount / limit);

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setActivityType('all');
    setEntityType('all');
    setUserEmail('');
    setPage(1);
  };

  return (
    <div className="space-y-6" data-testid="activity-history-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-light text-white tracking-tight flex items-center gap-2">
            <History className="w-5 h-5 sm:w-6 sm:h-6 text-terracotta" />
            Activity History
          </h1>
          <p className="text-xs sm:text-base text-white/60 mt-1">Audit trail of all actions in the system</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchActivities}
          className="border-forest-600 w-full sm:w-auto"
          data-testid="refresh-activities"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-forest-800 border-forest-700">
        <CardHeader className="pb-4">
          <CardTitle className="text-sm font-medium text-white flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
            {/* Date Range */}
            <div>
              <label className="text-xs text-white/60 mb-1 block">Start Date</label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
                className="bg-forest-700 border-forest-600 text-white"
                data-testid="filter-start-date"
              />
            </div>
            <div>
              <label className="text-xs text-white/60 mb-1 block">End Date</label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
                className="bg-forest-700 border-forest-600 text-white"
                data-testid="filter-end-date"
              />
            </div>
            
            {/* Activity Type */}
            <div>
              <label className="text-xs text-white/60 mb-1 block">Activity Type</label>
              <Select value={activityType} onValueChange={(v) => { setActivityType(v); setPage(1); }}>
                <SelectTrigger className="bg-forest-700 border-forest-600" data-testid="filter-activity-type">
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {activityTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {ActivityTypeLabel(type)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Entity Type */}
            <div>
              <label className="text-xs text-white/60 mb-1 block">Entity Type</label>
              <Select value={entityType} onValueChange={(v) => { setEntityType(v); setPage(1); }}>
                <SelectTrigger className="bg-forest-700 border-forest-600" data-testid="filter-entity-type">
                  <SelectValue placeholder="All entities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Entities</SelectItem>
                  {entityTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* User Email */}
            <div>
              <label className="text-xs text-white/60 mb-1 block">User Email</label>
              <Input
                type="text"
                placeholder="Search by email..."
                value={userEmail}
                onChange={(e) => { setUserEmail(e.target.value); setPage(1); }}
                className="bg-forest-700 border-forest-600 text-white"
                data-testid="filter-user-email"
              />
            </div>
          </div>
          
          {/* Clear Filters */}
          {(startDate || endDate || activityType !== 'all' || entityType !== 'all' || userEmail) && (
            <div className="mt-4 flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearFilters}
                className="text-white/60 hover:text-white"
                data-testid="clear-filters"
              >
                Clear Filters
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results Count */}
      <div className="flex items-center justify-between text-sm text-white/60">
        <span>
          Showing {activities.length} of {totalCount} activities
        </span>
        {totalPages > 1 && (
          <span>
            Page {page} of {totalPages}
          </span>
        )}
      </div>

      {/* Activity List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-terracotta" />
        </div>
      ) : error ? (
        <Card className="bg-forest-800 border-forest-700">
          <CardContent className="py-12">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <p className="text-white/60">{error}</p>
            </div>
          </CardContent>
        </Card>
      ) : activities.length === 0 ? (
        <Card className="bg-forest-800 border-forest-700">
          <CardContent className="py-12">
            <div className="text-center">
              <History className="w-12 h-12 text-white/20 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">No Activity Found</h3>
              <p className="text-white/60">No activities match your current filters.</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-forest-800 border-forest-700">
          <CardContent className="p-0">
            <div className="divide-y divide-forest-700">
              {activities.map((activity) => {
                const Icon = getActivityIcon(activity.activity_type);
                return (
                  <div
                    key={activity.id}
                    className="p-4 hover:bg-forest-700/30 transition-colors"
                    data-testid={`activity-${activity.id}`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Icon */}
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${getActivityColor(activity.activity_type).split(' ')[0]}`}>
                        <Icon className={`w-5 h-5 ${getActivityColor(activity.activity_type).split(' ')[1]}`} />
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="text-white font-medium">{activity.description}</p>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <Badge 
                                variant="outline" 
                                className={`text-xs ${getActivityColor(activity.activity_type)}`}
                              >
                                {ActivityTypeLabel(activity.activity_type)}
                              </Badge>
                              {activity.entity_name && (
                                <span className="text-sm text-white/60">
                                  • {activity.entity_name}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-xs text-white/40">
                              {formatDateTime(activity.created_at)}
                            </p>
                          </div>
                        </div>
                        
                        {/* User Info */}
                        <div className="flex items-center gap-2 mt-2 text-sm text-white/60">
                          <User className="w-3 h-3" />
                          <span>{activity.user_name || activity.user_email}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="border-forest-600"
            data-testid="prev-page"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Previous
          </Button>
          
          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (page <= 3) {
                pageNum = i + 1;
              } else if (page >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }
              
              return (
                <Button
                  key={pageNum}
                  variant={page === pageNum ? "default" : "outline"}
                  size="sm"
                  onClick={() => setPage(pageNum)}
                  className={page === pageNum ? "bg-terracotta hover:bg-terracotta/90" : "border-forest-600"}
                  data-testid={`page-${pageNum}`}
                >
                  {pageNum}
                </Button>
              );
            })}
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="border-forest-600"
            data-testid="next-page"
          >
            Next
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
};
